import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { authenticate, signup } from "../feature/auth/authAPI";
import { Link, useNavigate } from "react-router-dom";
import Loader from "../Components/loader";
import { Video, Mail, Lock, User as UserIcon, ArrowRight } from "lucide-react";

export default function Signup() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, authenticated } = useSelector((state) => state.auth);
  const { loading, success: msg } = useSelector((state) => state.auth.signup);
  const { loading: authLoading } = useSelector((state) => state.auth.authenticate);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "candidate",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(signup(form));
  };

  useEffect(() => {
    dispatch(authenticate());
  }, [dispatch]);

  useEffect(() => {
    if (authenticated && user) {
      navigate("/dashboard");
    }
  }, [user, msg, loading, authenticated, navigate]);

  useEffect(() => {
    if (msg && !authenticated) {
      dispatch(authenticate());
    }
  }, [msg, authenticated, dispatch]);

  useEffect(() => {
    document.title = "Interviewly - Sign Up";
  }, []);

  return loading || authLoading ? (
    <Loader />
  ) : (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-cocoa-800 px-4 py-12 sm:px-6">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-[radial-gradient(ellipse_at_top,rgba(251,191,36,0.1),transparent_60%)]"
        aria-hidden="true"
      />
      <div className="relative w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-400 text-cocoa-900 shadow-gold-inset">
            <Video className="h-7 w-7" strokeWidth={2.25} />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-cream-100 sm:text-4xl">
            Welcome to Interviewly
          </h1>
          <p className="mx-auto mt-3 max-w-md text-base leading-relaxed text-amber-100/60">
            Create your account to join as an{" "}
            <span className="font-semibold text-amber-200">Interviewer</span> or{" "}
            <span className="font-semibold text-amber-200">Candidate</span> and
            experience seamless, real-time interviews.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="card animate-fade-in-up p-7 shadow-modal sm:p-8"
        >
          <div className="mb-6">
            <h2 className="text-xl font-semibold tracking-tight text-cream-100">
              Create your account
            </h2>
            <p className="mt-1 text-sm text-amber-100/50">
              Takes less than a minute
            </p>
          </div>

          <div className="flex flex-col gap-5">
            <div>
              <label htmlFor="name" className="label">
                Name
              </label>
              <div className="relative">
                <UserIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-amber-200/40" />
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={form.name}
                  onChange={handleChange}
                  className="input pl-10"
                  placeholder="Enter your name"
                  autoComplete="name"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="label">
                Email
              </label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-amber-200/40" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  className="input pl-10"
                  placeholder="you@company.com"
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="label">
                Password
              </label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-amber-200/40" />
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={form.password}
                  onChange={handleChange}
                  className="input pl-10"
                  placeholder="Enter your password"
                  autoComplete="new-password"
                />
              </div>
            </div>

            <div>
              <label htmlFor="role" className="label">
                Role
              </label>
              <select
                id="role"
                name="role"
                value={form.role}
                onChange={handleChange}
                className="input cursor-pointer appearance-none"
              >
                <option value="interviewer" className="bg-cocoa-700">
                  Interviewer
                </option>
                <option value="candidate" className="bg-cocoa-700">
                  Candidate
                </option>
              </select>
            </div>
          </div>

          <button type="submit" className="btn btn-primary btn-lg mt-7 w-full">
            Sign Up
            <ArrowRight className="h-4 w-4" />
          </button>

          <p className="mt-6 text-center text-sm text-amber-100/50">
            Already have an account?{" "}
            <Link
              to="/login"
              className="font-semibold text-amber-300 transition-colors hover:text-amber-200"
            >
              Log in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
